import { useTranslation } from '@govard/i18n-core/react';
import { EntityStatusChip } from '../../../../components/entity-detail/EntityStatusChip.js';
import { getDealStageTone } from '../../../../shared/format/enum-labels.js';
import { i18n } from '../../../../i18n/index.js';
import { useState, type ReactElement } from 'react';
import { ChevronDown, ChevronUp, Kanban } from 'lucide-react';
import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

interface DealSummarySnippetProps {
  summary: Extract<StructuredMessagePayload, { type: 'deal_summary' }>['summary'];
  onOpenEntity?:
    | ((type: 'deal' | 'contact', id: string, title?: string | undefined) => void)
    | undefined;
}

const STAGE_LABEL_KEYS: Record<string, string> = {
  New: 'crm.deals.stage.New',
  Contacted: 'crm.deals.stage.Contacted',
  Negotiation: 'crm.deals.stage.Negotiation',
  Won: 'crm.deals.stage.Won',
  Lost: 'crm.deals.stage.Lost',
};

function formatAmount(amount: number, currency: string, locale: string = 'ru'): string {
  return `${new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US').format(amount)} ${currency}`;
}

export function DealSummarySnippet({
  summary,
  onOpenEntity,
}: DealSummarySnippetProps): ReactElement {
  const { t, locale } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const stageLabel = t(STAGE_LABEL_KEYS[summary.deal.stage] ?? summary.deal.stage);

  return (
    <div
      data-testid="structured-snippet-deal-summary"
      className="my-1.5 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-2xs transition-all"
    >
      {/* Super-light Single-line Horizontal Header */}
      <div
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-xs hover:bg-stone-50"
      >
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800">
            <Kanban size={13} />
          </div>
          <span
            onClick={(e) => {
              if (onOpenEntity) {
                e.stopPropagation();
                onOpenEntity('deal', summary.deal.id, summary.deal.title);
              }
            }}
            className="font-semibold text-stone-900 hover:text-amber-700 hover:underline"
          >
            {summary.deal.title}
          </span>
          <EntityStatusChip tone={getDealStageTone(summary.deal.stage)}>
            {stageLabel}
          </EntityStatusChip>
          <span className="font-semibold text-stone-800 text-[11px]">
            {formatAmount(summary.deal.amount, summary.deal.currency, locale)}
          </span>
        </div>

        <button
          type="button"
          className="flex items-center gap-1 text-[11px] font-medium text-stone-500 hover:text-stone-800"
        >
          <span>
            {isExpanded
              ? i18n.t('crm.ai_chat.widget.collapse')
              : i18n.t('crm.ai_chat.widget.expand')}
          </span>
          {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {/* Expandable Details Accordion */}
      {isExpanded && (
        <div className="border-t border-stone-100 p-3 text-xs space-y-2.5 bg-[var(--color-surface)]">
          {summary.contact && (
            <div className="rounded-lg border border-stone-200/80 bg-white p-2.5">
              <span className="text-[11px] font-semibold text-stone-500 uppercase">
                {t('crm.chat.deal_summary.contact')}
              </span>
              <p className="font-medium text-stone-900 mt-0.5">{summary.contact.fullName}</p>
            </div>
          )}

          {summary.recentActivities.length > 0 && (
            <div className="space-y-1">
              <span className="text-[11px] font-semibold text-stone-500 uppercase">
                {i18n.t(
                  'crm.features.features.ai.chat.components.snippets.dealsummarysnippet.sobytiya',
                )}
              </span>
              <ul className="space-y-1">
                {summary.recentActivities.map((act) => (
                  <li
                    key={act.id}
                    className="rounded-lg border border-stone-200/80 bg-white px-2.5 py-1.5 text-stone-700"
                  >
                    <span className="font-medium text-stone-800">{act.subject}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
